/**
 * useReapplyWorkflow Hook
 *
 * A React hook for managing the reapply workflow feature via the API.
 * Provides methods for preview, execute, and fetching import configs.
 */

import { useState, useCallback } from 'react';
import type {
  ReapplyPreviewRequest,
  ReapplyPreviewResult,
  ReapplyExecuteRequest,
  ReapplyExecuteResult,
  ReapplyImportConfigOption,
  ReapplyImportConfigsResponse,
} from '@/types/accounting';

export interface UseReapplyWorkflowReturn {
  /** Preview result from the last preview request */
  preview: ReapplyPreviewResult | null;
  /** Available import configs for scope selection */
  importConfigs: ReapplyImportConfigOption[];
  /** Whether an API request is in progress */
  loading: boolean;
  /** Error message from the last failed request */
  error: string | null;
  /** Preview the reapply workflow to see what will be affected */
  previewReapply: (request: ReapplyPreviewRequest) => Promise<ReapplyPreviewResult>;
  /** Execute the reapply workflow */
  executeReapply: (request: ReapplyExecuteRequest) => Promise<ReapplyExecuteResult>;
  /** Fetch available import configs for scope selection */
  getImportConfigs: () => Promise<ReapplyImportConfigOption[]>;
  /** Clear any error state */
  clearError: () => void;
  /** Clear the preview result */
  clearPreview: () => void;
}

/**
 * Convert camelCase request to snake_case API request body
 */
function toApiRequestBody(
  request: ReapplyPreviewRequest | ReapplyExecuteRequest
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    scope: request.scope,
    include_manually_classified: request.include_manually_classified,
  };

  if (request.import_config_ids) {
    body.import_config_ids = request.import_config_ids;
  }

  if (request.date_range) {
    body.date_range = {
      start: request.date_range.start,
      end: request.date_range.end,
    };
  }

  return body;
}

/**
 * Hook for managing the reapply workflow
 *
 * @example
 * ```typescript
 * const {
 *   preview,
 *   importConfigs,
 *   loading,
 *   error,
 *   previewReapply,
 *   executeReapply,
 *   getImportConfigs
 * } = useReapplyWorkflow();
 *
 * // Fetch import configs on mount
 * useEffect(() => {
 *   getImportConfigs();
 * }, [getImportConfigs]);
 *
 * // Preview the workflow
 * const handlePreview = async () => {
 *   const result = await previewReapply({
 *     scope: 'all',
 *     include_manually_classified: false
 *   });
 *   console.log(`${result.preview.transaction_count} transactions will be affected`);
 * };
 *
 * // Execute the workflow
 * const handleExecute = async () => {
 *   const result = await executeReapply({
 *     scope: 'all',
 *     include_manually_classified: false
 *   });
 *   console.log(result.message);
 * };
 * ```
 */
export function useReapplyWorkflow(): UseReapplyWorkflowReturn {
  const [preview, setPreview] = useState<ReapplyPreviewResult | null>(null);
  const [importConfigs, setImportConfigs] = useState<ReapplyImportConfigOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearPreview = useCallback(() => {
    setPreview(null);
  }, []);

  /**
   * Preview the reapply workflow
   */
  const previewReapply = useCallback(
    async (request: ReapplyPreviewRequest): Promise<ReapplyPreviewResult> => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch('/api/accounting/reapply_workflow/preview', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(toApiRequestBody(request)),
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('You need to sign in or sign up before continuing.');
          }
          throw new Error(`Failed to preview reapply workflow: ${response.statusText}`);
        }

        const result = (await response.json()) as ReapplyPreviewResult;
        setPreview(result);
        return result;
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Failed to preview reapply workflow';
        setError(message);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Execute the reapply workflow
   */
  const executeReapply = useCallback(
    async (request: ReapplyExecuteRequest): Promise<ReapplyExecuteResult> => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch('/api/accounting/reapply_workflow/execute', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(toApiRequestBody(request)),
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('You need to sign in or sign up before continuing.');
          }
          throw new Error(`Failed to execute reapply workflow: ${response.statusText}`);
        }

        const result = (await response.json()) as ReapplyExecuteResult;
        // Clear preview after successful execution
        setPreview(null);
        return result;
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Failed to execute reapply workflow';
        setError(message);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Fetch available import configs for scope selection
   */
  const getImportConfigs = useCallback(async (): Promise<ReapplyImportConfigOption[]> => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/accounting/import_configs');

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('You need to sign in or sign up before continuing.');
        }
        throw new Error(`Failed to fetch import configs: ${response.statusText}`);
      }

      const data = (await response.json()) as ReapplyImportConfigsResponse;
      setImportConfigs(data.import_configs);
      return data.import_configs;
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to fetch import configs';
      setError(message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    preview,
    importConfigs,
    loading,
    error,
    previewReapply,
    executeReapply,
    getImportConfigs,
    clearError,
    clearPreview,
  };
}
